<template>
  <q-dialog
    :model-value="modelValue"
    :content-class="[isTouch ? 'touch-device' : '', $attrs['content-class']]"
    :transition-show="mediaQueries.mobile ? 'slide-up' : 'fade'"
    :transition-hide="mediaQueries.mobile ? 'slide-down' : 'fade'"
    :maximized="isTab && isMaximized"
    :seamless="isSeamless"
    no-route-dismiss
    :position="position"
    :style="zIndex ? `z-index: ${zIndex}` : null"
    @update:model-value="closeModal"
  >
    <component
      :is="modalTemplate"
      v-bind="props"
      :style="{ width: modalWidth }"
      :class="{ 'custom-modal--big-size': isBigSize }"
      @on-ok="onOk"
      @close-modal="closeModal"
      @back="backToRegistrationModal"
    >
      <template #closeButton>
        <div v-if="!$slots.header && !title" class="custom-modal__close-btn" @click="closeModal">
          <Icon name="close" />
        </div>
      </template>
      <template #header>
        <div v-if="$slots.header || title" class="custom-modal__header-wrapper">
          <div v-if="title" class="custom-modal__header" v-html="title" />
          <slot name="header" />
          <div class="custom-modal__close-btn" @click="closeModal">
            <Icon name="close" />
          </div>
        </div>
      </template>
      <template #body>
        <div
          v-if="bodyHtml"
          class="custom-modal__body"
          :class="{ 'custom-modal__body--ios': $helpers.platform.is.ios }"
          v-html="bodyHtml"
        />
        <slot v-else-if="customBody"></slot>
        <div v-else class="custom-modal__body" :class="{ 'custom-modal__body--ios': $helpers.platform.is.ios }">
          <slot></slot>
          {{ body }}
        </div>
        <div v-if="!footer && actions" class="custom-modal__actions">
          <ol-button v-if="hasClose" :label="$t('close')" color="gray-night-100" @click="closeModal" />
          <ol-button
            v-for="(button, index) in actions"
            :key="index"
            :class="button.class"
            v-bind="button.props"
            @click="button.action"
          />
        </div>
      </template>
      <template v-if="hasFooter" #footer>
        <ol-button v-if="hasClose" :label="$t('close')" color="gray-night-100" @click="closeModal" />
        <ol-button
          v-for="(button, index) in actions"
          :key="index"
          v-bind="button"
          :loading="loadingObject[index]"
          :disabled="button.disabled"
          @click="() => modalAction(index, button)"
        />
        <slot name="footer"></slot>
      </template>
    </component>
  </q-dialog>
</template>

<script>
import UIButton from 'components/ui/UIButton.vue'
import { toggleOverflowBody } from 'src/common/functions'
import { defineAsyncComponent } from 'vue'
import { postMessageFromWebviewToApp } from 'src/common/webview'
import { OlButton } from '@gamesite/olimp-core'
import { useSiteInfoStore } from 'src/stores/site-info'

export default {
  name: 'CustomModal',
  components: { OlButton, UIButton },
  inject: ['mediaQueries', 'appModal'],
  props: {
    modelValue: Boolean,
    hasClose: Boolean,
    isMaximized: {
      type: Boolean,
      default: true,
    },
    isBigSize: Boolean,
    title: {
      type: String,
      default: null,
    },
    body: {
      type: String,
      default: null,
    },
    bodyHtml: {
      type: String,
      default: null,
    },
    footer: {
      type: Boolean,
      default: true,
    },
    customBody: {
      type: Boolean,
      default: false,
    },
    actions: {
      type: Array,
      default: null,
    },
    componentName: {
      type: [Function, Object],
      default: null,
    },
    props: {
      type: Object,
      default: () => ({}),
    },
    resolve: {
      type: Function,
      default: null,
    },
    reject: {
      type: Function,
      default: null,
    },
    isSeamless: {
      type: Boolean,
      default: false,
    },
    position: {
      type: String,
      default: 'standard',
    },
    zIndex: {
      type: Number,
      default: null,
    },
  },
  emits: ['update:model-value'],
  data() {
    return {
      loading: false,
      loadingObject: {},
    }
  },
  computed: {
    isTab() {
      return this.mediaQueries.mobile
    },
    modalWidth() {
      if (this.isTab) {
        return '100%'
      }
      return this.isBigSize ? '934px' : '450px'
    },
    modalTemplate() {
      return this.componentName ? this.componentName : defineAsyncComponent(() => import('./ModalTemplate.vue'))
    },
    hasFooter() {
      return (this.actions && this.actions.length) || (this.$slots.footer && this.footer) || this.hasClose
    },
    isTouch() {
      return this.$helpers.platform.has.touch
    },
  },
  watch: {
    actions: {
      handler(data) {
        if (Array.isArray(data)) {
          data.forEach((action, id) => {
            this.loadingObject[id] = false
          })
        }
      },
      immediate: true,
    },
  },
  methods: {
    onOk(event) {
      this.resolve(event)
      this.closeModal()
    },
    async modalAction(index, data) {
      if (!data.action) {
        throw new Error('Action is not exist')
      }
      if (data.loading) {
        this.loadingObject[index] = true
        try {
          await data.action()
        } catch {
          throw new Error('You must use Promise action with loading props === true')
        } finally {
          this.closeModal()
        }
      } else {
        if (data.action !== 'close') {
          data.action()
        }
        this.closeModal()
      }
    },
    backToRegistrationModal() {
      this.appModal.open({
        componentName: defineAsyncComponent(() => import('components/modals/AuthModalNew.vue')),
        props: { tab: 'reg' },
      })
    },
    onCancel(value) {
      if (this.reject) {
        this.reject(value)
      }
    },
    closeModal(value) {
      document.documentElement.style.scrollBehavior = 'auto'
      setTimeout(() => {
        document.documentElement.style.scrollBehavior = null
      }, 100)
      if (useSiteInfoStore().isWebView) {
        postMessageFromWebviewToApp({ type: 'modal', value: 'closed' })
      }
      this.loadingObject = {}
      this.loading = false
      this.onCancel(value)
      toggleOverflowBody(false)
      this.$emit('update:model-value')
      if (this.$route.query.modal) {
        this.$router.replace({ 'query.modal': null })
      }
    },
  },
}
</script>

<style lang="scss">
.q-dialog__backdrop {
  background-color: rgba(#121212, 0.6);

  .messages-modal & {
    background-color: transparent;
  }
}

.q-dialog__inner {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0;

  .custom-modal {
    border-radius: var(--radius-3);
  }

  &:not(.q-dialog__inner--maximized) .custom-modal {
    @media (max-width: 768px) {
      border-radius: var(--radius-3) var(--radius-3) 0 0;
    }
  }

  @media (min-height: 1024px) {
    padding: calc(2% + 25px) 0;
  }

  &.q-dialog__inner--maximized .custom-modal {
    min-height: 100vh;
    @supports (height: 100dvh) {
      min-height: 100dvh;
    }
  }
  &.q-dialog__inner--bottom:not(.q-dialog__inner--animating) > div {
    @include tab {
      border-radius: 10px;
    }
  }
  &--bottom {
    justify-content: flex-end;
    @include tab {
      justify-content: center;
    }
  }
}

.messages-modal {
  background: none;

  .custom-modal {
    max-height: 640px !important;
  }

  .q-dialog__inner {
    @media (min-width: 768px) {
      padding: 50px 10px;
      overflow: auto;
    }
  }

  .custom-modal__close-btn {
    top: 18px;
  }

  .custom-modal__header-wrapper {
    border-bottom: 1px solid var(--cas-light-200);
    height: 64px;
    padding: 16px 56px 16px 16px !important;
    min-height: auto;
  }

  .custom-modal__header {
    display: flex;
    align-items: center;
  }

  .custom-modal__body {
    padding: 16px;
  }

  .custom-modal__footer {
    padding: 16px;
    min-height: auto;
    @media (max-width: 767px) {
      padding-bottom: 24px;
    }
  }

  .q-dialog__inner {
    justify-content: flex-end;
    align-items: flex-start;
    flex-direction: row;
  }
}

.touch-device {
  .q-dialog__inner {
    pointer-events: all !important;
  }
}

.custom-modal {
  width: 100%;
  background-color: var(--cas-main-deep-2);
  color: var(--cas-light-900);
  border-radius: var(--radius-3);
  overflow: visible !important;
  display: flex;
  flex-direction: column;
  position: relative;
  max-height: 100% !important;
  max-width: none !important;
  @media (min-width: 768px) {
    min-width: 450px;
    max-width: 80vw !important;
  }

  &__header-wrapper {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-height: 64px;
    color: var(--gray-night-900, rgba(255, 255, 255, 0.9));
    font-size: 24px;
    line-height: 32px;
    font-weight: 700;
    border-radius: var(--radius-3) var(--radius-3) 0 0;
    flex: none;
    @include mobile {
      border-radius: 0;
    }
    &:not(:empty) {
      background-color: var(--cas-main-deep-3);
      height: auto;
      padding: 16px 56px 16px 16px;

      @media (min-width: 1024px) {
        padding-left: 24px;
        padding-right: 24px;
      }

      @media (min-width: 768px) {
        padding: 24px 56px 24px 24px;
      }
    }
  }

  &__header {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1 1;
  }

  &__back-btn {
    display: flex;
    align-items: center;
    column-gap: 4px;
    width: 75px;
    margin: 20px 0 -17px;
    color: rgba(255, 255, 255, 0.7);
    font-size: 16px;
    font-weight: 700;
    line-height: 20px;
    cursor: pointer;

    @media (max-width: 767px) {
      margin: 20px 0 -12px;
    }
  }

  &__close-btn {
    width: 24px;
    height: 24px;
    position: absolute;
    margin: auto;
    right: 16px;
    top: 20px;
    flex-shrink: 0;
    font-size: 24px;
    display: flex;
    align-items: center;
    cursor: pointer;
    color: var(--cas-light-500);
    transition: 0.3s ease;

    i {
      height: 24px;
    }

    z-index: 1;

    &:active {
      color: var(--cas-light-700);
    }

    @media (min-width: 600px) {
      &:hover {
        color: var(--cas-light-700);
      }
    }

    @media (min-width: 768px) {
      right: 20px;
      top: 24px;
    }
  }

  &__body {
    font-size: 16px;
    padding: 16px;
    overflow: auto;
    flex: 1 1;

    p {
      margin-bottom: 1em;
    }

    @media (min-width: 768px) {
      padding: 24px;
    }

    &--ios {
      padding-bottom: 56px;
    }
  }

  &__actions {
    display: flex;
    margin-top: 24px;
    flex-direction: column;
    gap: 16px;

    .custom-modal--big-size & {
      @media (min-width: 768px) {
        flex-direction: row;
        justify-content: flex-end;
        & > button {
          flex: 1 1;
        }
      }
    }
  }

  &__footer {
    flex: none;
    padding: 16px 16px 24px 16px;
    border-top: 1px solid var(--cas-light-200);
    display: flex;
    flex-direction: column;
    gap: 8px;

    @media (min-width: 768px) {
      padding: 24px;
    }

    @media (min-width: 600px) {
      justify-content: flex-start;
      flex-direction: row-reverse;
      flex-wrap: wrap;
    }
  }
}
</style>
